import * as z from "zod";

import { API_ENDPOINT, WEB_ENDPOINT } from "shared/client/endpoint";
import magic from "shared/client/magic";
import logger from "shared/logger";
import { ProfileSchema, StoredProfile } from "shared/types/models";

const MAGIC_KEY = process.env.NEXT_PUBLIC_MAGIC_API_KEY ?? "";

export const QuerySchema = z.object({
  magic_credential: z.string().optional(),
  magic_email: z.string().email().optional(),
});
export type Query = z.infer<typeof QuerySchema>;

// Does the full login flow for a page context (web page)
export const loginForWeb = async (magic_credential: string): Promise<StoredProfile | undefined> => {
  const token = await getLoginToken(magic_credential);
  if (token === null) {
    logger.debug("no token found");
    return undefined;
  } else {
    logger.debug({ token }, "exchanging login token");
    return await exchangeLoginToken(token);
  }
};

// Can only be used on a page context (content script or web page)
export const getLoginToken = async (magic_credential: string): Promise<string | null> => {
  logger.debug({ magic_credential }, "getting login token with credential");
  return await magic(MAGIC_KEY).auth.loginWithCredential(magic_credential);
};

// Can be used in the background script
export const exchangeLoginToken = async (token: string): Promise<StoredProfile> => {
  const res = await fetch(`${API_ENDPOINT}/auth/login`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: "Bearer " + token,
    },
    credentials: "include",
  });

  if (res.status !== 200) {
    throw new Error(`Expected 200, got ${res.status}: ${res.statusText}`);
  }

  const raw = await res.json();
  const profile = ProfileSchema.parse(raw);

  logger.debug({ profile }, "successfully logged in");

  const jwt = res.headers.get("Authorization");
  if (!jwt) {
    throw new Error("no token in header");
  }

  return { profile, jwt };
};

export const sendMagicLinkForWeb = (magic_email: string) => {
  logger.debug({ magic_email }, "sending magic link email for website login");

  return magic(MAGIC_KEY).auth.loginWithMagicLink({
    email: magic_email,
    redirectURI: `${WEB_ENDPOINT}/auth`,
  });
};

export const sendMagicLinkForExtension = (magic_email: string) => {
  logger.debug({ magic_email }, "sending magic link email for browser extension login");

  return magic(MAGIC_KEY).auth.loginWithMagicLink({
    email: magic_email,
  });
};

export const isLoggedIn = async () => magic(MAGIC_KEY).user.isLoggedIn();

export const loggedInEmail = async () => {
  const user = await magic(MAGIC_KEY).user.getMetadata();

  return user.email;
};

export const logout = async () => magic(MAGIC_KEY).user.logout();
